import type { EffectiveUpstreamWord, UpstreamAttributeView, UpstreamWordRule } from '@fitting-room/contracts'
import { useRuleSyncWord, useSyncWords } from '../../../lib/adminQueries'
import { attributeNoun } from './words'

/**
 * "Words from Airtable" on the Catalog page — every wording the sync knows,
 * and what it becomes here.
 *
 * Two kinds, said apart on every row: the built-in folds a developer wrote
 * from the 2026-09-04 export, and the answers the boutique has given since on
 * the Sync gowns screen. Both can be changed here — changing a built-in makes
 * it the boutique's answer — and the boutique's can be removed: removing one
 * puts a built-in fold for the same word back, or, for a word with none,
 * sends it back to "New words from Airtable" on the next scan.
 *
 * Changing a built-in is allowed although one pick on `Sweetheart` moves 84
 * gowns, because nothing moves until the next sync, and that sync's scan
 * shows every gown it would change, row by row, before anything is written.
 * The built-ins sit folded away under their count so that is not done by a
 * stray tap.
 *
 * Owner only, like the sync it belongs to. The caller decides whether to draw
 * it; this asks the server only when drawn.
 */
export function WordsSection({ staffToken }: { readonly staffToken: string | null }) {
  const vocabulary = useSyncWords(staffToken)

  return (
    <section className="mt-8">
      <h2 className="st-eyebrow mb-2">Words from Airtable</h2>
      <p className="st-meta mb-3 max-w-2xl">
        How each word in Airtable&rsquo;s Silhouette, Neckline and Detail columns reads here. Built-in words came
        with the system; the rest are answers given on Sync gowns. Changes take effect on the next sync.
      </p>
      {vocabulary.isError ? <p className="st-alert mb-4">Couldn&rsquo;t load the words from Airtable.</p> : null}
      {vocabulary.isPending ? <p className="st-meta">Loading…</p> : null}
      {vocabulary.data
        ? vocabulary.data.attributes.map((attribute) => (
            <AttributeWords
              key={attribute.key}
              staffToken={staffToken}
              attribute={attribute}
              words={vocabulary.data.words.filter((w) => w.attribute === attribute.key)}
            />
          ))
        : null}
    </section>
  )
}

function describeRule(rule: UpstreamWordRule, attribute: UpstreamAttributeView): string {
  if (rule.action === 'ignore') return 'Ignored'
  return attribute.options.find((o) => o.value === rule.value)?.label ?? rule.value
}

function AttributeWords({
  staffToken,
  attribute,
  words,
}: {
  readonly staffToken: string | null
  readonly attribute: UpstreamAttributeView
  readonly words: readonly EffectiveUpstreamWord[]
}) {
  const boutique = words.filter((w) => w.source === 'boutique')
  const builtIn = words.filter((w) => w.source === 'built_in')

  return (
    <div className="mb-5">
      <h3 className="mb-1 text-sm font-medium">{attribute.label}</h3>
      {boutique.length === 0 ? (
        <p className="st-meta mb-2 text-sm">No answers from the boutique yet.</p>
      ) : (
        <ul className="mb-2 space-y-2">
          {boutique.map((word) => (
            <WordRow key={word.wording} staffToken={staffToken} attribute={attribute} word={word} />
          ))}
        </ul>
      )}
      <details className="text-sm">
        <summary className="st-meta cursor-pointer">
          {builtIn.length} built-in {builtIn.length === 1 ? 'word' : 'words'}
        </summary>
        <ul className="mt-2 space-y-2">
          {builtIn.map((word) => (
            <WordRow key={word.wording} staffToken={staffToken} attribute={attribute} word={word} />
          ))}
        </ul>
      </details>
    </div>
  )
}

function WordRow({
  staffToken,
  attribute,
  word,
}: {
  readonly staffToken: string | null
  readonly attribute: UpstreamAttributeView
  readonly word: EffectiveUpstreamWord
}) {
  const rule = useRuleSyncWord(staffToken)
  const noun = attributeNoun(attribute, attribute.key)
  const current = word.rule.action === 'ignore' ? '' : word.rule.value
  const mine = word.source === 'boutique'
  const removeLabel = word.builtIn ? `Back to built-in (${describeRule(word.builtIn, attribute)})` : 'Remove'

  return (
    <li className="st-card flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <span className="min-w-40 flex-1 font-medium">&ldquo;{word.wording}&rdquo;</span>
      <label className="flex min-w-0 max-w-full items-center gap-2 text-sm">
        <span className="st-meta shrink-0">reads as</span>
        <select
          className="st-select min-w-0 max-w-full"
          aria-label={`What "${word.wording}" reads as`}
          value={current}
          disabled={rule.isPending}
          onChange={(event) => {
            const value = event.target.value
            rule.mutate({
              kind: 'set',
              body: {
                attribute: attribute.key,
                wording: word.wording,
                rule: value ? { action: 'map', value } : { action: 'ignore' },
              },
            })
          }}
        >
          <option value="">Ignored</option>
          {attribute.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.addedByBoutique ? ' (added by the boutique)' : ''}
            </option>
          ))}
        </select>
      </label>
      {mine ? (
        <>
          <span className="st-badge st-badge--submitted">Set by the boutique</span>
          <button
            type="button"
            className="st-btn-quiet"
            disabled={rule.isPending}
            title={word.builtIn ? undefined : `The next scan will ask about this ${noun} again.`}
            onClick={() => rule.mutate({ kind: 'remove', body: { attribute: attribute.key, wording: word.wording } })}
          >
            {removeLabel}
          </button>
        </>
      ) : (
        <span className="st-badge st-badge--none">Built in</span>
      )}
      {word.rule.action === 'map' && word.rule.lossy ? (
        <span className="st-meta w-full text-xs">Airtable&rsquo;s word is kept for stylists, since ours says less.</span>
      ) : null}
      {rule.isError ? (
        <p className="st-alert w-full" role="alert">
          {rule.error.message}
        </p>
      ) : null}
    </li>
  )
}
